import React, { Suspense, useEffect, useState } from 'react';
import Select, { OnChangeValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import StepWizard from 'react-step-wizard';
import { Form, Spinner } from 'react-bootstrap';
import { Filter, Option, TableComponentProps } from '../../models/table-models';
import { Pagination, usePagination } from '../../utils/pagination-hooks';
import { CustomStepWizardNav } from '../../components/CustomStepWizardNav';
import { AddActionComponent } from '../../components/AddActionComponent';
import { AddEditPassport } from './AddEditPassport';
import { PassportDetails } from './PassportDetails';
import { AnyPassport, PassportType } from '../../models/warehouse-models';
import { useGetAllPassportsByType } from '../../utils/warehouses-hooks';
import { useTableData } from '../../utils/data-hooks';
import { resourceCache } from 'use-async-resource';
import { Role } from '../../utils/Enums/Roles';
import { PassportTable } from './PassportTable';

const passportSelectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 3000 }),
};

export const TableSuspenseContainer: React.FC = ({ children }) => {
    return (
        <Suspense fallback={<div className='d-flex justify-content-center py-3'><Spinner animation='border' /></div>}>
            <div className='d-flex align-items-center flex-column'>{children}</div>
        </Suspense>
    );
};

export const PassportsTypeFilter = ({
    filter,
    setFilter,
    pagination,
}: {
    filter: Filter;
    setFilter: React.Dispatch<React.SetStateAction<Filter>>;
    pagination: Pagination;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const emptyOption = { name: t('all'), value: '' };
    const options = [
        emptyOption,
        ...PassportType.map((value) => ({
            name: t(value.toLowerCase()),
            value,
        })),
    ];
    const selectedOption = options.find(({ value }) => value === (filter.type ?? '')) ?? emptyOption;

    const updateTypeFilter = (option: OnChangeValue<Option, false>) => {
        setFilter((currentFilter) => ({
            ...currentFilter,
            type: option?.value || undefined,
        }));
        pagination.setCurrentPage(1);
    };

    return (
        <Form.Group className='mb-0 passport-type-filter' controlId='passportTypeFilter'>
            <Form.Label>{t('passportType')}</Form.Label>
            <Select
                value={selectedOption}
                options={options}
                getOptionLabel={({ name }) => name}
                getOptionValue={({ value }) => value}
                onChange={updateTypeFilter}
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={passportSelectStyles}
            />
        </Form.Group>
    );
};

const usePassportStatusTable = (
    archived: boolean,
    filter: Filter,
    pageSize: number
) => {
    const pagination = usePagination(pageSize);
    const tableFilter = { ...filter, archived };
    const [getEntities, fetchNewEntities] = useTableData<AnyPassport[]>(
        useGetAllPassportsByType,
        { ...pagination, pageSize },
        tableFilter
    );

    const refetchEntitiesHook = async () => {
        await fetchNewEntities({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pageSize,
            pagination,
            filter: tableFilter,
        });
    };

    return {
        getEntities,
        refetchEntitiesHook,
        pagination,
    };
};

export const Passports = ({ filter, setFilter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [selectedItem, setSelectedItem] = useState<AnyPassport>();

    const activePassports = usePassportStatusTable(false, filter, pagination.pageSize);
    const inactivePassports = usePassportStatusTable(true, filter, pagination.pageSize);

    const refetchAllEntitiesHook = async () => {
        resourceCache(useGetAllPassportsByType).clear();
        await Promise.all([
            activePassports.refetchEntitiesHook(),
            inactivePassports.refetchEntitiesHook(),
        ]);
    };

    useEffect(() => {
        activePassports.pagination.setCurrentPage(1);
        inactivePassports.pagination.setCurrentPage(1);
        activePassports.pagination.setPagination({ ...pagination });
        inactivePassports.pagination.setPagination({ ...pagination });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination]);

    return (
        <>
            <PassportDetails title={t('passport')} item={selectedItem} setSelectedItem={setSelectedItem} />
            <div className='d-flex w-100 justify-content-between align-items-end flex-column flex-lg-row gap-2 mb-3'>
                <AddActionComponent
                    accessActionRoles={[Role.ADMIN, Role.WAREHOUSE]}
                    title={t('addPassport')}
                    addComponent={AddEditPassport}
                    refetchEntitiesHook={refetchAllEntitiesHook}
                    wrapperClassName=''
                />
                <PassportsTypeFilter filter={filter} setFilter={setFilter} pagination={pagination} />
            </div>
            <StepWizard
                className='d-flex flex-wrap w-100 full-width-table justify-content-center align-items-center'
                nav={<CustomStepWizardNav steps={['activePassports', 'inactivePassports']} />}
                transitions={{
                    enterRight: '',
                    enterLeft: '',
                    exitRight: '',
                    exitLeft: '',
                }}
            >
                <TableSuspenseContainer>
                    <PassportTable
                        archived={false}
                        setSelectedItem={setSelectedItem}
                        {...activePassports}
                        refetchEntitiesHook={refetchAllEntitiesHook}
                    />
                </TableSuspenseContainer>

                <TableSuspenseContainer>
                    <PassportTable
                        archived={true}
                        setSelectedItem={setSelectedItem}
                        {...inactivePassports}
                        refetchEntitiesHook={refetchAllEntitiesHook}
                    />
                </TableSuspenseContainer>
            </StepWizard>
        </>
    );
};
